import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  API, cn, formatDate, DEPARTMENT_COLORS, DEPARTMENT_LABELS,
  STATUS_COLORS, STATUS_LABELS, isOverdue, getDaysUntil,
} from "../lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, Clock, AlertTriangle, Calendar, Package, ImageIcon,
  PlayCircle, RefreshCw, Filter, ChevronRight, Loader2,
  ArrowLeft, Search,
} from "lucide-react";

export default function TaskListDetailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const taskName = searchParams.get("task_name") || "";
  const department = searchParams.get("department") || "";
  const isMyMode = searchParams.get("mode") === "my" || window.location.pathname.includes("/tasks/my/");

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingTask, setUpdatingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchTasks();
  }, [taskName, department, isMyMode]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      let url;
      if (isMyMode) {
        url = `${API}/tasks/my-tasks`;
      } else {
        const params = new URLSearchParams();
        if (department) params.append("department", department);
        url = `${API}/tasks${params.toString() ? "?" + params.toString() : ""}`;
      }
      const response = await axios.get(url, { withCredentials: true });
      // Filter by task_name client-side
      const filtered = taskName
        ? response.data.filter((t) => t.task_name === taskName)
        : response.data;
      setTasks(filtered);
    } catch (err) {
      console.error("Failed to fetch tasks:", err);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    setUpdatingTask(taskId);
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`Task marked as ${STATUS_LABELS[newStatus]}`);
      fetchTasks();
    } catch (err) {
      toast.error("Failed to update task status");
    } finally {
      setUpdatingTask(null);
    }
  };

  const getFilteredTasks = () => {
    let result = tasks;
    if (filterStatus === "overdue") {
      result = result.filter((t) => isOverdue(t.target_date) && t.status !== "completed");
    } else if (filterStatus !== "all") {
      result = result.filter((t) => t.status === filterStatus);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (t) =>
          (t.order_number || "").toLowerCase().includes(q) ||
          (t.item_name || "").toLowerCase().includes(q) ||
          (t.task_name || "").toLowerCase().includes(q)
      );
    }
    return result;
  };

  const filteredTasks = getFilteredTasks();
  const pendingCount = tasks.filter((t) => t.status === "pending").length;
  const inProgressCount = tasks.filter((t) => t.status === "in_progress").length;
  const completedCount = tasks.filter((t) => t.status === "completed").length;
  const overdueCount = tasks.filter((t) => isOverdue(t.target_date) && t.status !== "completed").length;

  const backPath = isMyMode ? "/tasks/my" : "/tasks";

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-9 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="task-list-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8" onClick={() => navigate(backPath)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl tracking-tight">{taskName || "Tasks"}</h1>
          <p className="text-muted-foreground text-sm">
            {department && <span className="capitalize">{DEPARTMENT_LABELS[department] || department}</span>}
            {department && " — "}{tasks.length} total tasks
          </p>
        </div>
      </div>

      {/* Stat pills */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatPill label="Overdue" count={overdueCount} color={overdueCount > 0 ? "text-red-600 bg-red-50 border-red-200" : ""} icon={AlertTriangle} />
        <StatPill label="Pending" count={pendingCount} color={pendingCount > 0 ? "text-amber-600 bg-amber-50 border-amber-200" : ""} icon={Clock} />
        <StatPill label="In Progress" count={inProgressCount} color={inProgressCount > 0 ? "text-blue-600 bg-blue-50 border-blue-200" : ""} icon={PlayCircle} />
        <StatPill label="Completed" count={completedCount} color="text-emerald-600 bg-emerald-50 border-emerald-200" icon={CheckCircle2} />
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by order number, item, task..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-9 pl-10 pr-4 rounded-sm border border-input bg-background text-sm"
            data-testid="task-detail-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px] rounded-sm h-9" data-testid="task-detail-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task List */}
      <Card className="border border-border shadow-none rounded-sm">
        <CardHeader className="pb-2">
          <CardDescription>{filteredTasks.length} tasks</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No tasks match the current filter</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTasks.map((task) => {
                const daysUntil = getDaysUntil(task.target_date);
                const overdue = isOverdue(task.target_date);
                const deptColors = DEPARTMENT_COLORS[task.department] || DEPARTMENT_COLORS.merchandizing;
                const statusColors = STATUS_COLORS[task.status] || STATUS_COLORS.pending;

                return (
                  <div
                    key={task.task_id}
                    className={cn(
                      "flex items-center gap-4 p-3 rounded-sm border transition-colors hover:bg-muted/30 cursor-pointer",
                      overdue && task.status !== "completed" && "border-red-200 bg-red-50/40"
                    )}
                    onClick={() => navigate(`/tasks/${task.task_id}`)}
                    data-testid={`task-row-${task.task_id}`}
                  >
                    {/* Item Thumbnail */}
                    <div className="w-10 h-10 rounded bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {task.item_image_url ? (
                        <img
                          src={task.item_image_url.startsWith("/api") ? `${API.replace("/api", "")}${task.item_image_url}` : task.item_image_url}
                          alt={task.item_name}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling?.classList.remove('hidden'); }}
                        />
                      ) : null}
                      <Package className={`w-4 h-4 text-muted-foreground/50 ${task.item_image_url ? 'hidden' : ''}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-medium text-sm">{task.task_name}</span>
                        {overdue && task.status !== "completed" && (
                          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Overdue</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="font-mono">{task.order_number}</span>
                        <span>{task.item_name}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Badge variant="outline" className={cn("text-[10px]", deptColors.light)}>
                          {DEPARTMENT_LABELS[task.department]}
                        </Badge>
                        <Badge variant="secondary" className={cn("text-[10px]", statusColors.light)}>
                          {STATUS_LABELS[task.status]}
                        </Badge>
                        <span className={cn("text-xs flex items-center gap-1", overdue ? "text-red-500" : "text-muted-foreground")}>
                          <Calendar className="w-3 h-3" />
                          {formatDate(task.target_date)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {task.status === "pending" && (
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => updateTaskStatus(task.task_id, "in_progress")} disabled={updatingTask === task.task_id}>
                          {updatingTask === task.task_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><PlayCircle className="w-3 h-3 mr-1" />Start</>}
                        </Button>
                      )}
                      {task.status === "in_progress" && (
                        <Button size="sm" className="rounded-sm h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => updateTaskStatus(task.task_id, "completed")} disabled={updatingTask === task.task_id}>
                          {updatingTask === task.task_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><CheckCircle2 className="w-3 h-3 mr-1" />Complete</>}
                        </Button>
                      )}
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatPill({ label, count, color, icon: Icon }) {
  return (
    <div className={cn("flex items-center gap-2 p-3 rounded-sm border text-sm", color || "text-muted-foreground bg-muted/30")}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="font-semibold">{count}</span>
      <span className="text-xs">{label}</span>
    </div>
  );
}
